import { useCalculatorContext } from '@/components/DesignSystem/Subsections/Calculator/contexts';
import OccupationCardGrid from './OccupationCardGrid';
import DegreeLength from '@/components/DesignSystem/Subsections/DegreeLength';
import React, { useMemo, useContext, useRef } from 'react';
import RecommendedSection from './RecommendedSection';
import CalculatorFormPanel from './CalculatorFormPanel';
import OnetDisclaimer from '@/components/DesignSystem/Subsections/CareerPathwayPlanner/components/OnetDisclaimer';
import { DisclosureTooltip } from '@/components/Tooltip';
import { Button } from '@/components/Button';
import { MobiusContext } from '@/components/MobiusProvider';
import getUrlQuery from '@/utils/getUrlQuery';
import { buildQueryParamsWithDCS } from '@/utils/buildQueryParamsWithDCS';
import uuid from '@/utils/uuid';
import isVoyagerCombo from '@/utils/isVoyagerCombo';
import { getCurrentFormStateNames } from '@/utils/formStateUtils';
import getTrafficSource from '@/utils/getTrafficSource';
import getAliasParams from '@/utils/getAliasParams';
import { TagularProduct, TagularWebElement } from '@/types/Tagular';
import tagularEvent from '@/utils/tagular/tagularEvent';
import useViewEvents from '@/hooks/useViewEvent';
import useFlowTrackingEvents from '@/components/DynamicFlow/hooks/useFlowTrackingEvent';
import { useDynamicExperienceContext } from '@/components/DynamicFlow/providers/DynamicExperienceProvider';

// Component mapping based on variant
const getCalculatorComponent = (variant: string) => {
  switch (variant) {
    case 'salary':
      return <OccupationCardGrid />;
    case 'degree-length':
      return <DegreeLength />;
    default:
      return <></>;
  }
};

const CalculatorContent: React.FC = () => {
  const {
    state,
    handleFieldChange,
    updateCalculatorValue,
    variant,
    config,
    getCalculatorValue,
    triggerDataFetch,
    loadingPrograms,
    loadingOccupations,
    isCalculating,
    getSelectedDegree,
    getSelectedSubject,
    getRecommendedItems,
    trackingContext,
  } = useCalculatorContext();
  const { siteConfig } = useContext(MobiusContext);
  const { eventContext } = useDynamicExperienceContext();
  const { triggerProductClickedEvent } = useFlowTrackingEvents();
  const viewCorrelationIdRef = useRef(uuid());
  const viewMoreViewCorrelationIdRef = useRef(uuid());
  const findYourMatchViewCorrelationIdRef = useRef(uuid());
  // Use useViewEvents for tracking button views
  const { ref: viewMoreButtonRef } = useViewEvents([
    {
      name: 'ElementViewed',
      payload: {
        correlationId: viewMoreViewCorrelationIdRef.current,
        webElement: {
          elementType: 'dexp-tool-link',
          htmlId: 'view-more-button',
          location: config?.tracking?.location || '',
          name: 'view-more-button',
          position: 'Below Calculator Results',
          text: 'View More Programs',
        },
      },
    },
  ]);

  // Use useViewEvents for tracking button views
  const { ref: findYourMatch } = useViewEvents([
    {
      name: 'ElementViewed',
      payload: {
        correlationId: findYourMatchViewCorrelationIdRef.current,
        webElement: {
          elementType: 'dexp-tool-link',
          htmlId: 'voyager_dcs',
          location: config?.tracking?.location || '',
          name: 'voyager-entry',
          position: 'Below Calculator Results',
          text: 'Find Your Match',
        },
      },
    },
  ]);

  const currentDegreeSlug = getCalculatorValue('degree');
  const currentCategorySlug = getCalculatorValue('category');
  const currentSubjectSlug = getCalculatorValue('subject');

  const selectedCurrentEducation = getSelectedDegree();
  const selectedFieldOfStudy = getSelectedSubject();
  const recommendedItems = getRecommendedItems();

  const studyMatchDestination = useMemo(() => {
    const baseUrl = siteConfig?.studyMatchUrl || '';
    const params = buildQueryParamsWithDCS({
      siteConfig,
      formValues: {
        degree: currentDegreeSlug,
        category: currentCategorySlug,
        subject: currentSubjectSlug,
      },
      format: 'studymatch',
    });
    try {
      return getUrlQuery(baseUrl, params);
    } catch {
      return baseUrl;
    }
  }, [
    siteConfig?.studyMatchUrl,
    currentDegreeSlug,
    currentCategorySlug,
    currentSubjectSlug,
  ]);

  const hasPrograms = recommendedItems.length > 0;

  // Check if all DCS fields are selected
  const isDCSComplete =
    getCalculatorValue('degree') &&
    getCalculatorValue('category') &&
    getCalculatorValue('subject');

  // Get the appropriate component based on variant - memoized
  const CalculatorComponent = useMemo(
    () => getCalculatorComponent(variant || 'default'),
    [variant],
  );

  // Convert FormState to FormData format for the panel - memoized
  const initialValues = useMemo(() => {
    const formState = state.formState;
    const calculatorValues = state.calculatorValues;

    // Combine both form state and calculator values
    const combinedValues = {
      ...calculatorValues,
    };

    if (formState && 'current' in formState) {
      // FormState structure with current/options - use context helper functions
      combinedValues.degree = getCalculatorValue('degree');
      combinedValues.category = getCalculatorValue('category');
      combinedValues.subject = getCalculatorValue('subject');
    } else if (formState) {
      // Simple FormData structure
      Object.assign(combinedValues, formState);
    }

    return combinedValues;
  }, [
    state.formState,
    state.calculatorValues,
    getCalculatorValue('degree'),
    getCalculatorValue('category'),
    getCalculatorValue('subject'),
  ]);

  // Generate correlationId for RecommendedSection.
  const correlationId = useMemo(() => uuid(), []);

  const handleCtaButtonClick = (type: string, correlationId: string) => {
    const isStudyMatch = type === 'studymatch';
    // Determine the correct URL
    const targetUrl = isStudyMatch
      ? siteConfig?.studyMatchUrl
      : siteConfig?.voyagerUrl;

    // Get current form field values
    const currentValues = {
      degree: getCalculatorValue('degree') || '',
      category: getCalculatorValue('category') || '',
      subject: getCalculatorValue('subject') || '',
    };

    const isVoyagerComboResult = isVoyagerCombo(currentValues);

    // Get formatted names for Voyager URLs
    const currentLabels = getCurrentFormStateNames(state.formState);

    // Create base query parameters for outbound url
    const baseQueryParams = {
      publisher: siteConfig?.domain,
      correlationId,
      idToken: siteConfig?.identityToken,
      position: 'null',
      source: getTrafficSource(window.location.href),
      ...getAliasParams(),
    };

    let outboundUrl: string;

    if (isStudyMatch || !isVoyagerComboResult) {
      // For StudyMatch or non-Voyager combos, use regular parameters
      const studyMatchParams = {
        ...baseQueryParams,
        degree: currentValues.degree,
        category: currentValues.category,
        subject: currentValues.subject,
      };
      outboundUrl = getUrlQuery(targetUrl, studyMatchParams);
    } else {
      // For Voyager combos, use the formatted DCS parameters
      const voyagerParams = {
        ...baseQueryParams,
        'dcs[degrees][]': currentLabels.degree || currentValues.degree,
        'dcs[categories][]': currentLabels.category || currentValues.category,
        'dcs[subjects][]': currentLabels.subject || currentValues.subject,
      };
      outboundUrl = getUrlQuery(targetUrl, voyagerParams);
    }

    if (!eventContext.dynamicExperienceEventFlag) {
      // Create the web element for tracking
      const buttonWebElement: TagularWebElement = {
        elementType: 'dexp-tool-link',
        htmlId: isStudyMatch ? 'view-more-button' : 'voyager_dcs',
        location: config?.tracking?.location || '',
        name: isStudyMatch ? 'studymatch-entry' : 'voyager-entry',
        position: isStudyMatch ? 'Below Editorial Listings' : 'null',
        text: isStudyMatch ? 'View More Programs' : 'Find Your Match',
      };

      const basePayloadForProductClicked: TagularProduct = {
        brand: '',
        category: getCalculatorValue('category') || '',
        formatSubtype: `${config?.tracking?.formType}-alt-entry` || '',
        formatType: 'dexp-alt-monetization-entry',
        location: config?.tracking?.location || '',
        name: getCalculatorValue('subject') || '',
        positionEngine: 'null',
        variant: getCalculatorValue('degree') || '',
      };

      // Create the tracking event payload
      const clickEventPayload = {
        name: 'ElementClicked',
        payload: {
          webElement: buttonWebElement,
          outboundUrl: outboundUrl || '',
          actionOutcome: 'INTERNALLINK',
          correlationId: correlationId,
        },
      };

      const productClickedPayload = {
        name: 'ProductClicked',
        payload: {
          outboundUrl: outboundUrl || '',
          actionOutcome: 'INTERNALLINK',
          correlationId: correlationId,
          product: basePayloadForProductClicked,
          viewCorrelationId: viewCorrelationIdRef.current,
        },
      };

      // Fire the Tagular tracking event
      tagularEvent('ElementClicked', clickEventPayload.payload);

      if (!isStudyMatch) {
        tagularEvent('ProductClicked', productClickedPayload.payload);
      }
    } else {
      triggerProductClickedEvent({
        ...eventContext,
        customDimensions: [
          ...(eventContext.customDimensions || []),
          {
            key: 'ctaText',
            value: isStudyMatch ? 'View More Programs' : 'Find Your Match',
          },
        ],
      });
    }

    // Navigate to the URL
    if (targetUrl) {
      window.open(outboundUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className='bg-white max-w-4xl mx-auto border border-gray-200 rounded-2xl shadow-sm not-prose'>
      <div className='max-w-7xl mx-auto p-3 md:p-5'>
        {/* Header Section */}
        <div className='flex flex-row justify-between items-center'>
          <span className='text-xl md:text-2xl font-bold text-gray-900'>
            {config?.title}
          </span>
          <DisclosureTooltip
            position={'bottom'}
            label={'Learn more about our partners'}
            className='font-body-12 font-normal'
          />
        </div>
        <p className='text-gray-600 text-sm md:text-base mt-2 mb-3 md:mb-6'>
          {config?.subhed}
        </p>

        {/* Main Content */}
        <div className='flex flex-col gap-6'>
          {/* Calculator Education Selection Panel */}
          {/* config can probably be removed and read from config */}
          <CalculatorFormPanel
            className='p-5'
            showEditButton={true}
            initiallyEditable={false}
            viewCorrelationId={viewCorrelationIdRef.current}
          />

          {isCalculating || loadingPrograms || loadingOccupations ? (
            <div className='flex flex-col items-center justify-center py-12'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700 mb-4'></div>
              <p className='text-lg text-gray-600 font-medium'>
                Updating your results...
              </p>
              <p className='text-sm text-gray-500 mt-2'>
                This may take a few moments
              </p>
            </div>
          ) : (
            <>
              <div className='flex flex-col lg:flex-row gap-8'>
                <div className='flex-1 md:space-y-4'>{CalculatorComponent}</div>

                {hasPrograms && (
                  <div className='lg:max-w-[250px]'>
                    <RecommendedSection
                      studyMatchDestination={studyMatchDestination}
                      recommendedItems={recommendedItems}
                      title='Recommended Programs'
                      correlationId={correlationId}
                      selectedCurrentEducation={selectedCurrentEducation}
                      selectedFieldOfStudy={selectedFieldOfStudy}
                      showViewMore={false}
                      eventContext='salary-calculator'
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <div className='flex flex-col md:flex-row px-3 md:px-5 pb-4 md:pb-6 justify-between items-center gap-3'>
        <span className='font-body-18 font-bold flex-1 min-w-0 pr-2'>
          Discover the educational path that matches your goals.
        </span>
        <div className='flex flex-nowrap gap-2 shrink-0 w-full md:w-fit'>
          <div ref={viewMoreButtonRef as React.LegacyRef<HTMLDivElement>}>
            <Button
              theme='secondary'
              variant='contained'
              tw='py-1 px-2 md:py-2 md:px-4 rounded-lg whitespace-nowrap w-full justify-center'
              onClick={() => {
                handleCtaButtonClick(
                  'studymatch',
                  viewMoreViewCorrelationIdRef.current,
                );
              }}
            >
              View More Programs
            </Button>
          </div>
          <div ref={findYourMatch as React.LegacyRef<HTMLDivElement>}>
            <Button
              variant='outlined'
              tw='py-1 px-2 md:py-2 md:px-4 rounded-lg whitespace-nowrap w-full justify-center'
              onClick={() => {
                handleCtaButtonClick(
                  'voyager',
                  findYourMatchViewCorrelationIdRef.current,
                );
              }}
            >
              Find Your Match
            </Button>
          </div>
        </div>
      </div>
      <div className='p-3 md:p-5'>
        <OnetDisclaimer displayOnetData={variant !== 'degree-length'} />
      </div>
    </div>
  );
};

export default CalculatorContent;
